import React, { useMemo, useRef } from "react";
import { Tooltip } from "primereact/tooltip";
import { T } from "services/lang/LanguageService";
import { Instant, IsoFields, ZonedDateTime, ChronoUnit } from "@js-joda/core";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { useCompanyTimezone } from "hooks/useTimezone";
import { toInstant, toZonedDateTime, toZonedDateTimeOrNow } from "utils/time-utils";
import { ComponentTheme } from "utils/enums";
import { DiffType, DiffValueOptions } from "./ValueChangeIndicatorTypes";
import { formatNumber, getTextColor } from "../charts/utils";
import "./HorizontalBarChart.scss";

interface HorizontalBarChartProps {
  data: DatasetDataInfoResponse;
  diffType: DiffType;
  theme?: ComponentTheme;
  reportMode?: boolean;
  decimalPlaces?: number;
  startOfTheProject?: Instant;
  calculateFromStartOfTheProject?: boolean;
  setCurrentDiffValueOptions: (diffValueOptions: DiffValueOptions) => void;
  useWeekYearComparison?: boolean;
  refTime?: Instant;
}

const HorizontalBarChart = (props: HorizontalBarChartProps) => {
  const companyTimezone = useCompanyTimezone();

  const ref = useMemo(() => {
    const baseRef = toZonedDateTimeOrNow(companyTimezone, props.refTime ?? Instant.now());
    return baseRef.truncatedTo(ChronoUnit.DAYS);
  }, [companyTimezone, props.refTime]);

  const lastDiffRef = useRef<number | null>(null);

  const bars = useMemo(() => {
    const dataset = props.data.data ?? [];
    const grouped = new Map<string, number>();

    let projectStart: ZonedDateTime | null = null;
    if (props.calculateFromStartOfTheProject && props.startOfTheProject) {
      const startInstant = toInstant(props.startOfTheProject);
      projectStart = ZonedDateTime.ofInstant(startInstant, companyTimezone);
    }

    if (props.useWeekYearComparison) {
      let startingWeek = 1;

      const dateRangeStart = dataset[0]?.key;
      const dateRangeEnd = dataset[dataset.length - 1]?.key;
      const week = ref.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      const month = ref.monthValue();
      const year = month === 12 && week === 1 ? ref.year() + 1 : ref.year();

      if (projectStart) {
        const rangeStart = toZonedDateTime(dateRangeStart, companyTimezone);
        const rangeEnd = toZonedDateTime(dateRangeEnd, companyTimezone);

        if (
          projectStart.isBefore(rangeStart) ||
          projectStart.isAfter(rangeEnd) ||
          projectStart.year() < year
        ) {
          startingWeek = 1;
        } else {
          startingWeek = projectStart.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
        }
      }

      let currentWeek = ref.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);

      if (currentWeek <= 0) {
        const prevYear = ref.minusYears(1);
        currentWeek = prevYear.range(IsoFields.WEEK_OF_WEEK_BASED_YEAR).maximum();
      }

      const refWeek = currentWeek;

      dataset.forEach(point => {
        const dt = toZonedDateTime(point.key, companyTimezone);
        const isoWeekYear = dt.get(IsoFields.WEEK_BASED_YEAR);
        const dtWeek = dt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
        const dtMonth = dt.monthValue();
        const dtYear = dtMonth === 12 && dtWeek === 1 ? dt.year() + 1 : dt.year();
        if (dtYear <= year - 2) return;

        const currentIsoYear = ref.get(IsoFields.WEEK_BASED_YEAR);

        let include = false;
        if (isoWeekYear <= currentIsoYear) {
          include = dtWeek >= startingWeek && dtWeek <= refWeek;
        }

        if (include) {
          grouped.set(String(isoWeekYear), (grouped.get(String(isoWeekYear)) ?? 0) + (point.value ?? 0));
        }
      });
    } else {
      const firstDayOfCurrentMonth = ref
        .withDayOfMonth(1)
        .withHour(0)
        .withMinute(0)
        .withSecond(0)
        .withNano(0);
      const targetMonth = firstDayOfCurrentMonth.monthValue();

      const dateRangeStart = dataset[0]?.key;
      const dateRangeEnd = dataset[dataset.length - 1]?.key;

      dataset.forEach(point => {
        const dt = ZonedDateTime.parse(point.key).withZoneSameInstant(companyTimezone);
        const month = dt.monthValue();
        const week = dt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
        const year = month === 12 && week === 1 ? dt.year() + 1 : dt.year();

        let include = false;

        if (projectStart) {
          const rangeStart = toZonedDateTime(dateRangeStart, companyTimezone);
          const rangeEnd = toZonedDateTime(dateRangeEnd, companyTimezone);

          let projectStartForYear = projectStart.withYear(year).withZoneSameInstant(companyTimezone);

          const localDate = projectStartForYear.toLocalDate();
          if (
            projectStart.monthValue() === 2 &&
            projectStart.dayOfMonth() === 29 &&
            !localDate.isLeapYear()
          ) {
            projectStartForYear = projectStartForYear.withDayOfMonth(28);
          }

          if (projectStart.isBefore(rangeStart) || projectStart.isAfter(rangeEnd)) {
            include = dt.monthValue() <= targetMonth && dt.isBefore(ref);
          } else {
            include =
              (dt.isAfter(projectStartForYear) || dt.isEqual(projectStartForYear)) &&
              dt.monthValue() <= targetMonth;
          }
        } else {
          include = dt.monthValue() <= targetMonth;
        }

        if (include) {
          grouped.set(String(year), (grouped.get(String(year)) ?? 0) + (point.value ?? 0));
        }
      });
    }

    const sortedBars = [...grouped.entries()]
      .sort(([a], [b]) => {
        const [aYear, aWeek] = a.split(`, ${T("word.week")}`).map(Number);
        const [bYear, bWeek] = b.split(`, ${T("word.week")}`).map(Number);
        if (!Number.isNaN(aYear) && !Number.isNaN(bYear)) {
          if (aYear !== bYear) return aYear - bYear;
          if (!Number.isNaN(aWeek) && !Number.isNaN(bWeek)) return aWeek - bWeek;
        }
        return a.localeCompare(b);
      })
      .map(([label, value]) => ({ label, value }));

    if (sortedBars.length >= 2) {
      const first = sortedBars[0];
      const last = sortedBars[sortedBars.length - 1];
      const diffValue =
        props.diffType === DiffType.PERCENTAGE
          ? ((last.value - first.value) / first.value) * 100
          : last.value - first.value;

      if (diffValue !== lastDiffRef.current) {
        lastDiffRef.current = diffValue;
        props.setCurrentDiffValueOptions({
          value: diffValue,
          currentValueInfo: (
            <div className="flex flex-row gap-2">
              <b>{last.label}:</b>
              <div>
                {formatNumber(last.value, props.decimalPlaces)}
                {props.data.unit ? ` ${props.data.unit}` : ""}
              </div>
            </div>
          ),
          previousValueInfo: (
            <div className="flex flex-row gap-2">
              <b>{first.label}:</b>
              <div>
                {formatNumber(first.value, props.decimalPlaces)}
                {props.data.unit ? ` ${props.data.unit}` : ""}
              </div>
            </div>
          ),
          isHorizontalBarChartDiffLoaded: true,
        });
      }
    } else if (lastDiffRef.current !== 0) {
      lastDiffRef.current = 0;
      props.setCurrentDiffValueOptions({
        value: 0,
        currentValueInfo: <>–</>,
        previousValueInfo: <>–</>,
        isHorizontalBarChartDiffLoaded: true,
      });
    }

    return sortedBars;
  }, [
    props.data,
    props.diffType,
    props.decimalPlaces,
    props.data.unit,
    props.setCurrentDiffValueOptions,
    props.useWeekYearComparison,
    props.startOfTheProject,
    props.calculateFromStartOfTheProject,
    companyTimezone,
    ref,
  ]);

  const maxValue = Math.max(...bars.map(b => b.value), 0);

  return (
    <div className={`bar-chart-container${props.reportMode ? "-report-mode" : ""}`}>
      {bars.map((bar, i) => {
        const percentage = maxValue > 0 ? (bar.value / maxValue) * 100 : 0;
        const targetId = `bar-fill-${i}`;

        return (
          <div key={bar.label} className="bar-row">
            <Tooltip target={`#${targetId}`} />
            <div className="bar-wrapper">
              <div className="bar-track">
                <div
                  id={targetId}
                  className="bar-fill"
                  style={{ width: `${percentage}%` }}
                  data-pr-tooltip={bar.label}
                  data-pr-position="top"
                >
                  <span className="bar-label-inside">{bar.label}</span>
                </div>
              </div>

              <div className="bar-value" style={{ color: getTextColor(props.theme) }}>
                <div>{formatNumber(bar.value, props.decimalPlaces)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HorizontalBarChart;
